import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, Eye, EyeOff, UserCog, UserRound, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui";
import logo from "@/assets/logo.png";
import logoMark from "@/assets/logo-mark.png";

const DEMO = [
  { label: "Super Admin", email: "admin@medilearn.ae", password: "admin123", icon: UserCog },
  { label: "Reporting Head", email: "manager@medilearn.ae", password: "manager123", icon: Users },
  { label: "Employee", email: "employee@medilearn.ae", password: "employee123", icon: UserRound },
];

// Same pattern tile as the admin panel's login backdrop.
const PATTERN = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z' fill='%2323326d' fill-rule='evenodd'/%3E%3C/svg%3E\")";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/dashboard", { replace: true }); }, [user, nav]);

  const doLogin = async (em, pw) => {
    setError("");
    setLoading(true);
    try {
      await login(em, pw);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: PATTERN }} />
      <div className="relative">
        <div className="text-center mb-10 hidden md:block">
          <img src={logo} alt="MediLearn" className="h-12 w-auto m-auto" />
        </div>

        <div className="w-[90vw] md:w-full md:max-w-5xl grid lg:grid-cols-2 gap-6 items-stretch">
          <div className="hidden lg:flex flex-col justify-between neu-card p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-12 h-56 w-56 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-brand-blue/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <h2 className="text-3xl font-extrabold text-brand-blue leading-tight tracking-tight">
                Run your people
                <br />
                <span className="text-brand-orange">operations</span>
                <br />
                with confidence.
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed mt-4 mb-8 max-w-md">
                Leave, attendance, WPS-ready payroll and Emirates document compliance for your UAE workforce.
              </p>
              <div className="flex flex-wrap gap-2">
                {["WPS ready", "Ramadan hours", "30-day annual leave", "AED payroll"].map((t) => <span key={t} className="badge">{t}</span>)}
              </div>
            </div>
            <p className="relative text-slate-400 text-xs mt-10">&copy; {new Date().getFullYear()} MediLearn HRMS. All rights reserved.</p>
          </div>

          <div className="neu-card p-8 lg:p-10">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <img src={logoMark} alt="" className="h-9 w-9 rounded-md" />
              <img src={logo} alt="MediLearn" className="h-7 w-auto" />
            </div>
            <div className="mb-8">
              <h1 className="text-2xl font-extrabold text-brand-blue mb-1.5 tracking-tight">Welcome back</h1>
              <p className="text-slate-500 text-sm">Sign in to your HRMS account to continue.</p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }} className="space-y-4">
              <div>
                <label htmlFor="email" className="label">Email address</label>
                <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@medilearn.ae" autoComplete="username" required className="input" />
              </div>
              <div>
                <label htmlFor="password" className="label">Password</label>
                <div className="relative">
                  <input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" required className="input pr-11" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-blue transition-colors" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 p-3 rounded-2xl text-sm text-rose-600 animate-fade-in" style={{ background: "#FFE8EC", boxShadow: "inset 2px 2px 5px rgba(228,160,170,0.45), inset -2px -2px 5px rgba(255,255,255,0.85)" }}>
                  <AlertCircle size={15} className="shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <Button type="submit" variant="primary" size="md" fullWidth loading={loading} className="py-3 mt-2">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            <div className="neu-divider my-7" />
            <p className="label text-center">Demo accounts</p>
            <div className="space-y-2 mt-3">
              {DEMO.map(({ label, email: em, password: pw, icon: Icon }) => (
                <button
                  key={em}
                  type="button"
                  disabled={loading}
                  onClick={() => { setEmail(em); setPassword(pw); doLogin(em, pw); }}
                  className="w-full flex items-center gap-3 p-2.5 rounded-2xl text-left group transition-all hover:shadow-neu-xs disabled:opacity-60"
                >
                  <span className="h-9 w-9 rounded-xl shadow-neu-inset-xs flex items-center justify-center text-brand-blue shrink-0"><Icon size={15} /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-bold text-brand-blue group-hover:text-brand-orange transition-colors">{label}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{em}</span>
                  </span>
                  <ArrowRight size={14} className="text-slate-400 group-hover:text-brand-orange transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
